import {useCallback, useState} from 'react';
import {
  ChatConversation,
  renderMarkdownMessage,
  Suggestion,
  SuggestionsList,
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  Icons,
  cn,
} from '@continuum/ui';
import type {UseChatResult} from '@continuum/ui/hooks';

const DEFAULT_SUGGESTIONS = ['Summarize this page for me.'];

export interface AskAiPanelProps {
  chat: UseChatResult;
  className?: string;
  suggestions?: string[];
  placeholder?: string;
  advisorName?: string;
}

export const AskAiPanel = ({
  chat,
  className,
  suggestions = DEFAULT_SUGGESTIONS,
  placeholder = 'Ask anything...',
  advisorName,
}: AskAiPanelProps) => {
  const [inputValue, setInputValue] = useState('');

  const submit = useCallback(
    (text: string) => {
      if (chat.submitMessage(text)) {
        setInputValue('');
      }
    },
    [chat],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submit(inputValue);
    },
    [inputValue, submit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit(inputValue);
      }
    },
    [inputValue, submit],
  );

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-4 rounded-2xl border border-border bg-background p-4',
        className,
      )}
    >
      <ChatConversation
        messages={chat.messages}
        isAwaitingResponse={chat.isAwaitingResponse}
        error={chat.error}
        onRetry={chat.retry}
        renderMessage={renderMarkdownMessage}
        showScrollButton
        contentClassName="px-4 sm:px-5"
      />

      <div className="shrink-0 px-4 pb-3 sm:px-5 sm:pb-5">
        {chat.messages.length === 0 &&
          suggestions &&
          suggestions.length > 0 && (
            <SuggestionsList className="items-start pb-4">
              {suggestions.map((suggestion) => (
                <Suggestion
                  key={suggestion}
                  suggestion={suggestion}
                  onClick={submit}
                  className="w-fit rounded-lg bg-background"
                />
              ))}
            </SuggestionsList>
          )}
        <PromptInput
          onSubmit={handleSubmit}
          className="rounded-xl border-border p-2 pl-4 transition-colors focus-within:border-ring-muted focus-within:ring-2 focus-within:ring-border bg-accent"
        >
          <div className="flex items-end gap-2">
            <PromptInputTextarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={1}
              className="min-h-5 py-1.5"
            />
            <PromptInputSubmit
              disabled={!inputValue.trim() || chat.isSubmitting}
              className="size-8 shrink-0 rounded-full text-white"
            >
              <Icons.arrowUp className="size-4" />
            </PromptInputSubmit>
          </div>
        </PromptInput>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {advisorName
            ? `${advisorName}'s AI can make mistakes. Verify important details with them directly.`
            : 'AI can make mistakes. Verify important details with your advisor.'}
        </p>
      </div>
    </div>
  );
};
